import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './CustomerModal.css';

function CustomerModal({ isOpen, onClose, onSave, customer = null }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
  });
  const [nameError, setNameError] = useState('');
  const [formError, setFormError] = useState('');
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        phone: customer.phone || '',
        email: customer.email || '',
        address: customer.address || '',
        notes: customer.notes || '',
      });
    } else {
      setFormData({
        name: '',
        phone: '',
        email: '',
        address: '',
        notes: '',
      });
    }
    setNameError('');
    setFormError('');
  }, [customer, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name' && nameError) {
      setNameError('');
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setNameError(t('customers.validation.nameRequired'));
      nameInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      nameInputRef.current?.focus();
      return;
    }

    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      console.error('Error saving customer:', err);
      if (err.response?.status === 409) {
        setFormError(t('customers.validation.phoneExists'));
      } else {
        setFormError(t('common.error'));
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content customer-modal">
        <div className="modal-header">
          <h2>{customer ? t('customers.editTitle') : t('customers.addTitle')}</h2>
          <button type="button" className="close-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="modal-body">
            <div className={`form-group${nameError ? ' has-error' : ''}`}>
              <label>{t('customers.fields.name')} *</label>
              <input
                ref={nameInputRef}
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t('customers.placeholders.name')}
                required
                aria-required="true"
                aria-invalid={!!nameError}
                autoFocus
              />
              {nameError && (
                <span className="field-error" role="alert">
                  {nameError}
                </span>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('customers.fields.phone')}</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t('customers.placeholders.phone')}
                />
              </div>
              <div className="form-group">
                <label>{t('customers.fields.email')}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('customers.placeholders.email')}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('customers.fields.address')}</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder={t('customers.placeholders.address')}
              />
            </div>

            <div className="form-group">
              <label>{t('customers.fields.notes')}</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder={t('customers.placeholders.notes')}
                rows="3"
              />
            </div>

            {formError && <p className="error-text">{formError}</p>}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary">
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CustomerModal;
